import { supabase } from './supabase';
import { TokenPackage, TokenTransaction } from '../types/auth';

// Cache for user token balances to reduce database calls
const tokenCache = new Map<string, { balance: number; timestamp: number }>();
const CACHE_DURATION = 300000; // 5 minutes cache - increased to reduce database calls significantly

// Debounce map to prevent rapid successive calls for the same user
const pendingCalls = new Map<string, Promise<number>>();

// Database connection health tracking
let lastTokenHealthCheck = 0;
let isTokenDatabaseHealthy = true;

// Get cached token balance or fetch from database
const getCachedTokenBalance = (userId: string): number | null => {
  const cached = tokenCache.get(userId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('💰 Using cached token balance for user:', userId);
    return cached.balance;
  }
  return null;
};

// Set cached token balance
const setCachedTokenBalance = (userId: string, balance: number): void => {
  tokenCache.set(userId, { balance, timestamp: Date.now() });
};

// Clear cache for a user
const clearUserCache = (userId: string): void => {
  tokenCache.delete(userId);
  pendingCalls.delete(userId);
};

// Check database health for token operations
const checkTokenDatabaseHealth = async (): Promise<boolean> => {
  const now = Date.now();
  if (now - lastTokenHealthCheck < 30000) { // Cache health check for 30 seconds
    return isTokenDatabaseHealthy;
  }
  
  try {
    const { error } = await supabase
      .from('users')
      .select('count')
      .limit(1);
    
    lastTokenHealthCheck = now;
    isTokenDatabaseHealthy = !error;
    console.log(`🏥 Token database health check: ${isTokenDatabaseHealthy ? '✅ Healthy' : '❌ Unhealthy'}`);
    return isTokenDatabaseHealthy;
  } catch (error) {
    lastTokenHealthCheck = now;
    isTokenDatabaseHealthy = false;
    console.log('❌ Token database health check failed:', error);
    return false;
  }
};

// Retry utility with exponential backoff
const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error = new Error('Unknown error');
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Check if it's a timeout error that we should retry
      const errorMessage = (error as Error).message.toLowerCase();
      if (!errorMessage.includes('timeout') && !errorMessage.includes('network')) {
        throw error; // Don't retry non-timeout errors
      }
      
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`🔄 Retry attempt ${attempt + 1}/${maxRetries + 1} after ${delay}ms delay`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Database query with timeout wrapper
const queryWithTimeout = async <T>(
  queryFunction: () => Promise<T>,
  timeoutMs: number = 10000
): Promise<T> => {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Database query timeout')), timeoutMs);
  });

  return Promise.race([queryFunction(), timeoutPromise]);
};

export const tokenService = {

  // Check if user exists (read-only check, no creation due to RLS restrictions)
  async ensureUserExists(userId: string, email?: string): Promise<void> {
    try {
      await retryOperation(async () => {
        await queryWithTimeout(async () => {
          const { error } = await supabase
            .from('users')
            .select('id')
            .eq('id', userId)
            .single();

          if (error && error.code !== 'PGRST116') {
            // PGRST116 = no rows returned, which means user doesn't exist yet
            console.error('❌ Error checking if user exists:', error);
            throw error;
          }
        }, 8000);
      }, 2);
      
    } catch (error) {
      console.error('💥 Error checking user existence:', error);
      console.warn('⚠️ Continuing despite user check error...');
    }
  },

  // Get all active token packages with retry logic
  async getTokenPackages(): Promise<TokenPackage[]> {
    return retryOperation(async () => {
      const { data, error } = await queryWithTimeout(async () => {
        return supabase.from('token_packages').select('*').eq('is_active', true);
      });
      
      if (error) throw error;
      return data || [];
    });
  },

  // Get user's current token balance with improved error handling
  async getUserTokenBalance(userId: string, email?: string): Promise<number> {
    // Check cache first
    const cachedBalance = getCachedTokenBalance(userId);
    if (cachedBalance !== null) {
      return cachedBalance;
    }
    
    // Check if there's already a pending call for this user (debounce)
    const existingCall = pendingCalls.get(userId);
    if (existingCall) {
      console.log('🔄 Reusing existing token balance call for user:', userId);
      return existingCall;
    }
    
    console.log('🏦 Fetching token balance for user ID:', userId);
    
    // Create a new promise for this call
    const tokenBalancePromise = this.fetchTokenBalanceInternal(userId);
    pendingCalls.set(userId, tokenBalancePromise);
    
    try {
      const tokens = await tokenBalancePromise;
      return tokens;
    } finally {
      // Clean up the pending call
      pendingCalls.delete(userId);
    }
  },

  // Internal method to actually fetch token balance
  async fetchTokenBalanceInternal(userId: string): Promise<number> {
    try {
      // Check database health first
      const isHealthy = await checkTokenDatabaseHealth();
      if (!isHealthy) {
        console.warn('⚠️ Token database appears unhealthy, returning 0 tokens');
        return 0;
      }

      // Get token balance with retry logic - no need to ensure user exists as the trigger handles creation
      const tokens = await retryOperation(async () => {
        const { data, error } = await queryWithTimeout(async () => {
          return supabase
            .from('users')
            .select('tokens')
            .eq('id', userId)
            .single();
        }, 12000); // Increased timeout to 12 seconds for more reliability

        if (error) {
          // Handle case where user doesn't exist yet (trigger might be delayed)
          if (error.code === 'PGRST116') {
            console.log('⏳ User record not found, trigger may still be processing. Returning 0 tokens.');
            return 0;
          }
          console.error('❌ Database error getting token balance:', error);
          throw error;
        }

        return (data && (data as any).tokens) ? (data as any).tokens : 0;
      }, 2);
      
      // Cache the result
      setCachedTokenBalance(userId, tokens);
      
      return tokens;
    } catch (error) {
      console.error('💥 Exception in getUserTokenBalance:', error);
      console.warn('⚠️ Returning 0 tokens due to exception');
      return 0; // Return 0 instead of throwing
    }
  },

  // Get user's token transaction history with retry logic
  async getTokenTransactions(userId: string): Promise<TokenTransaction[]> {
    return retryOperation(async () => {
      const { data, error } = await queryWithTimeout(async () => {
        return supabase
          .from('token_transactions')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
      });

      if (error) throw error;
      return data || [];
    });
  },

  // Add tokens to user (for purchases) with retry logic
  async addTokens(userId: string, amount: number, description: string, referenceId?: string): Promise<void> {
    await retryOperation(async () => {
      const { error } = await queryWithTimeout(async () => {
        return supabase
          .from('token_transactions')
          .insert({
            user_id: userId,
            transaction_type: 'purchase',
            amount: amount,
            description: description,
            reference_id: referenceId
          });
      });

      if (error) throw error;
    });

    // Clear both token and user service caches when tokens are added
    clearUserCache(userId);
    try {
      const { userService } = await import('./userService');
      userService.clearUserCache(userId);
      console.log('🔄 Cleared both token and user service caches after token purchase');
    } catch (error) {
      // userService might not be available, that's ok
    }
  },

  // Spend tokens (for image generation) with retry logic
  async spendTokens(userId: string, amount: number, description: string): Promise<boolean> {
    const result = await retryOperation(async () => {
      const { data, error } = await queryWithTimeout(async () => {
        return supabase.rpc('spend_user_tokens', {
          p_user_id: userId,
          p_amount: amount,
          p_description: description
        });
      });

      if (error) throw error;
      return data;
    });
    
    // Clear cache when tokens are spent to ensure fresh data
    if (result) {
      clearUserCache(userId);
      // Also clear userService cache if it exists
      try {
        const { userService } = await import('./userService');
        userService.clearUserCache(userId);
      } catch (error) {
        // userService might not be available, that's ok
      }
    }
    
    return result;
  },

  // Format price from cents to dollars
  formatPrice(priceInCents: number): string {
    return (priceInCents / 100).toFixed(2);
  },

  // Calculate tokens per dollar for a package
  getTokensPerDollar(tokens: number, priceInCents: number): number {
    return tokens / (priceInCents / 100);
  },

  // Database health monitoring utilities
  async checkDatabaseHealth(): Promise<{ healthy: boolean; latency: number; error?: string }> {
    const startTime = Date.now();
    try {
      await queryWithTimeout(async () => {
        return supabase.from('users').select('count').limit(1);
      }, 5000); // 5 second timeout for health check
      
      const latency = Date.now() - startTime;
      return { healthy: true, latency };
    } catch (error) {
      const latency = Date.now() - startTime;
      return { 
        healthy: false, 
        latency, 
        error: (error as Error).message 
      };
    }
  },

  // Get cache statistics for debugging
  getCacheStats(): { size: number; entries: Array<{ userId: string; age: number }>; pendingCalls: number } {
    const entries = Array.from(tokenCache.entries()).map(([userId, data]) => ({
      userId,
      age: Date.now() - data.timestamp
    }));
    
    return {
      size: tokenCache.size,
      entries,
      pendingCalls: pendingCalls.size
    };
  },

  // Clear all caches (useful for debugging)
  clearAllCaches(): void {
    tokenCache.clear();
    pendingCalls.clear();
    console.log('🧹 All token service caches cleared');
  },

  // Add token package (admin function)
  async addTokenPackage(name: string, tokens: number, price_cents: number): Promise<any> {
    return retryOperation(async () => {
      const { data, error } = await queryWithTimeout(async () => {
        return supabase
          .from('token_packages')
          .insert({ name, tokens, price_cents })
          .select()
          .single();
      });

      if (error) throw error;
             return data;
     });
   }
}; 